/**
 * Parseur d'instructions vocales pour la saisie inventaire en français.
 *
 * Formats reconnus (très tolérant à l'OCR / accents / casse) :
 *   « quantité 2, péremption 04 2030 »
 *   « quantité deux péremption avril deux mille trente »
 *   « stock 5 expire 04/2030 »
 *   « 12, péremption 06-2029 »
 *
 * Retourne { quantity: number|null, expiration: 'YYYY-MM-DD'|null, raw: string }.
 */

const FR_NUMBERS = {
  zero: 0, zéro: 0, 'z é r o': 0, oh: 0,
  un: 1, une: 1,
  // « de » est ajouté comme alias de 2 : la reconnaissance vocale française
  // confond fréquemment « deux » (/dø/) avec « de » (/də/). Le contexte du
  // parseur (mot-clé « quantité » avant) garantit qu'on ne traite « de »
  // comme un nombre que dans un segment quantité, pas dans un texte général.
  deux: 2, de: 2,
  trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9,
  dix: 10, onze: 11, douze: 12, treize: 13, quatorze: 14, quinze: 15, seize: 16,
  vingt: 20, trente: 30, quarante: 40, cinquante: 50, soixante: 60,
  'soixante-dix': 70, septante: 70, 'quatre-vingt': 80,
  'quatre-vingts': 80, octante: 80, huitante: 80, 'quatre-vingt-dix': 90, nonante: 90,
  cent: 100, cents: 100, mille: 1000,
};

const FR_MONTHS = {
  janvier: 1, jan: 1, janv: 1,
  février: 2, fevrier: 2, fév: 2, fev: 2, févr: 2, févrierr: 2,
  mars: 3, mar: 3,
  avril: 4, avr: 4,
  mai: 5,
  juin: 6,
  juillet: 7, juil: 7,
  août: 8, aout: 8,
  septembre: 9, sept: 9, sep: 9,
  octobre: 10, oct: 10,
  novembre: 11, nov: 11,
  décembre: 12, decembre: 12, déc: 12, dec: 12,
};

const QUANTITY_KW = ['quantité', 'quantite', 'quantites', 'stock', 'physique', 'qté', 'qte', 'nombre'];
const PEREMPTION_KW = [
  'péremption', 'peremption', 'pérme', 'expiration', 'expire', 'expires',
  'exp', 'pér', 'per', 'expirent', 'date', 'caducité',
];

// Convertit une suite de mots français en nombre (jusqu'à 9999).
// Ex : ['deux', 'mille', 'trente'] → 2030 ; ['quatre'] → 4 ; ['zéro'] → 0
const wordsToNumber = (words) => {
  if (!words.length) return null;
  let total = 0;
  let current = 0;
  let matched = false;
  for (const w of words) {
    const norm = w.toLowerCase().replace(/[^\wàâäéèêëîïôöùûüÿç-]/g, '');
    if (FR_NUMBERS[norm] !== undefined) {
      matched = true;
      const v = FR_NUMBERS[norm];
      if (v === 100) current = (current || 1) * 100;
      else if (v === 1000) { total += (current || 1) * 1000; current = 0; }
      else current += v;
    } else if (/^\d+$/.test(norm)) {
      matched = true;
      current += parseInt(norm, 10);
    }
  }
  return matched ? total + current : null;
};

// Cherche le premier nombre (chiffres ou mots) dans un segment de texte
const extractNumber = (text) => {
  if (!text) return null;
  // 1. Nombre arabe direct (ex : « 12 », « 2030 »)
  const direct = text.match(/\b(\d{1,4})\b/);
  if (direct) return parseInt(direct[1], 10);
  // 2. Mots français
  const words = text.toLowerCase().split(/[\s,;.\-/]+/).filter(Boolean);
  return wordsToNumber(words);
};

// Cherche un mois (nom OU nombre 1-12) dans un segment de texte
const extractMonth = (text) => {
  if (!text) return null;
  const lower = text.toLowerCase();
  for (const [name, mo] of Object.entries(FR_MONTHS)) {
    const re = new RegExp(`\\b${name}\\b`);
    if (re.test(lower)) return mo;
  }
  // Nombre direct (1-12)
  const direct = text.match(/\b(0?\d|1[0-2])\b/);
  if (direct) {
    const v = parseInt(direct[1], 10);
    if (v >= 1 && v <= 12) return v;
  }
  // Mots → nombre
  const num = extractNumber(text);
  if (num !== null && num >= 1 && num <= 12) return num;
  return null;
};

// Cherche une année plausible (2020-2099) dans un segment de texte
const extractYear = (text) => {
  if (!text) return null;
  const direct = text.match(/\b(20\d{2})\b/);
  if (direct) return parseInt(direct[1], 10);
  // Année écrite : « deux mille trente », « vingt trente »
  const words = text.toLowerCase().split(/[\s,;.\-/]+/).filter(Boolean);
  const n = wordsToNumber(words);
  if (n && n >= 2020 && n <= 2099) return n;
  return null;
};

// Format date français complet : JJ/MM/AAAA ou MM/AAAA dans le texte brut
const extractFrenchDate = (text) => {
  // JJ/MM/AAAA
  const full = text.match(/\b(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})\b/);
  if (full) {
    const d = +full[1], mo = +full[2], y = +full[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && y >= 2020 && y <= 2099) {
      return { iso: `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`, hasDay: true };
    }
  }
  // MM/AAAA
  const my = text.match(/\b(\d{1,2})[\/\-.](\d{4})\b/);
  if (my) {
    const mo = +my[1], y = +my[2];
    if (mo >= 1 && mo <= 12 && y >= 2020 && y <= 2099) {
      return { iso: `${y}-${String(mo).padStart(2, '0')}-01`, hasDay: false };
    }
  }
  return null;
};

// Parseur principal
export const parseVoiceCommand = (raw) => {
  if (!raw || typeof raw !== 'string') {
    return { quantity: null, expiration: null, raw: '' };
  }
  // Normalisation : minuscules + suppression virgules / points superflus
  const text = raw.toLowerCase()
    .replace(/[«»"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // 1. Cherche d'abord une date au format JJ/MM/AAAA ou MM/AAAA (souvent dictée
  // de façon mécanique « 04 2030 » par les agents). On fait ça avant le découpage
  // pour éviter de fragmenter les chiffres.
  const directDate = extractFrenchDate(text);

  // 2. Découpe le texte en segments avant/après les mots-clés péremption
  const peremptionRegex = new RegExp(`\\b(${PEREMPTION_KW.join('|')})\\b`, 'i');
  const peremptionMatch = text.match(peremptionRegex);
  let quantitySegment = text;
  let peremptionSegment = '';
  if (peremptionMatch) {
    const idx = peremptionMatch.index;
    quantitySegment = text.slice(0, idx);
    peremptionSegment = text.slice(idx + peremptionMatch[0].length);
  }

  // 3. Cherche le mot-clé quantité dans le segment de gauche
  const quantityRegex = new RegExp(`\\b(${QUANTITY_KW.join('|')})\\b`, 'i');
  const quantityMatch = quantitySegment.match(quantityRegex);
  let quantityInput = quantitySegment;
  if (quantityMatch) {
    quantityInput = quantitySegment.slice(quantityMatch.index + quantityMatch[0].length);
  }

  const quantity = extractNumber(quantityInput);

  // 4. Date de péremption : si déjà trouvée au format direct dans tout le texte
  // ET située après le mot-clé péremption (ou pas de mot-clé), on la prend.
  let expiration = null;
  if (peremptionSegment) {
    const peremptionDirect = extractFrenchDate(peremptionSegment);
    if (peremptionDirect) {
      expiration = peremptionDirect.iso;
    } else {
      // Construction mois + année. On extrait d'abord l'année, puis on
      // cherche le mois UNIQUEMENT dans la partie de texte située avant
      // l'année (gère le cas « zéro quatre 2030 »).
      const y = extractYear(peremptionSegment);
      if (y) {
        const yMatch = peremptionSegment.match(new RegExp(`\\b${y}\\b`));
        const before = yMatch ? peremptionSegment.slice(0, yMatch.index) : peremptionSegment;
        let mo = extractMonth(before);
        if (!mo) {
          // Dernier recours : nombre 1-12 lu en mots dans la partie « before »
          const num = extractNumber(before);
          if (num !== null && num >= 1 && num <= 12) mo = num;
        }
        if (mo) expiration = `${y}-${String(mo).padStart(2, '0')}-01`;
      }
    }
  } else if (directDate) {
    // Pas de mot-clé péremption mais une date directe trouvée → on la prend
    expiration = directDate.iso;
  }

  return { quantity, expiration, raw };
};

// Vérifie si la SpeechRecognition API est disponible
export const isSpeechAvailable = () => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

// Démarre une session vocale ; retourne une promesse résolue avec le transcript.
// onPartial(text) reçoit les résultats intermédiaires (utile pour l'UI live).
export const recognizeVoice = ({ lang = 'fr-FR', onPartial } = {}) => new Promise((resolve, reject) => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return reject(new Error('SpeechRecognition non disponible sur ce navigateur.'));
  const rec = new SR();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 3;
  let finalText = '';
  rec.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) finalText += t;
      else interim += t;
    }
    if (onPartial) onPartial(finalText + interim);
  };
  rec.onerror = (e) => reject(new Error(e.error || 'Erreur reconnaissance vocale'));
  rec.onend = () => resolve(finalText.trim());
  try { rec.start(); } catch (e) { reject(e); }
  // Helper pour interruption manuelle
  rec.__stop = () => { try { rec.stop(); } catch (_) { /* ignore */ } };
  resolve.__rec = rec;
});
